/**
 * The item column (the prototype's `settingsList()`): heading with the category name and count, `+` when
 * the category creates records, the items filtered by the top-bar search and "Enabled items only".
 */

import { useNavigate } from 'react-router-dom';
import { Icon } from '../../icons/Icon.js';
import { Count } from '../../ui/Count.js';
import { Tool } from '../../ui/Tool.js';
import { ui, useUi } from '../../app/ui-store.js';
import type { ItemListProps, SettingsItem } from './types.js';

export type { ItemListProps } from './types.js';

export function filterItems(items: readonly SettingsItem[], search: string, enabledOnly: boolean): SettingsItem[] {
  const q = search.trim().toLowerCase();
  return items.filter((r) => r.name.toLowerCase().includes(q) && (!enabledOnly || r.enabled !== false));
}

export function ItemList({ category, items, isLoading, selectedId }: ItemListProps) {
  const navigate = useNavigate();
  const search = useUi((s) => s.settingsSearch);
  const enabledOnly = useUi((s) => s.settingsEnabledOnly);
  const shown = filterItems(items, search, enabledOnly);
  return (
    <section className="settings-list">
      <div className="pane-heading">
        <span>
          <Tool icon="menu" label="Settings categories" onClick={() => ui.set({ mobileOverlay: 'navigation' })} /> {category.name} <Count value={shown.length} />
        </span>
        {category.newLabel ? <Tool icon="plus" label="Add item" onClick={() => navigate(`/settings/${category.id}/new`)} /> : null}
      </div>
      <div className="setting-items">
        {shown.length ? (
          shown.flatMap((r) => [
            ...(r.groupHeading ? [<h3 key={`h:${r.id}`} className="setting-group-heading">{r.groupHeading}</h3>] : []),
            <button
              type="button"
              key={r.id}
              className={selectedId === r.id ? 'setting-item selected' : 'setting-item'}
              aria-current={selectedId === r.id ? 'true' : undefined}
              data-item-id={r.id}
              onClick={() => navigate(`/settings/${category.id}/${encodeURIComponent(r.id)}`)}
            >
              <span className="setting-item-icon">
                <Icon name={r.icon} />
              </span>
              <span>
                <strong>{r.name}</strong>
                {r.subtitle ? <small>{r.subtitle}</small> : null}
              </span>
              {r.status ? <span className="model-list-status">{r.status}</span> : null}
              {r.enabled !== undefined ? (
                <i
                  className={['item-status', r.enabled ? 'on' : '', r.tone ? `tone-${r.tone}` : ''].filter(Boolean).join(' ')}
                  title={r.tone === 'error' ? 'Failing' : r.tone === 'warn' ? 'Needs attention' : r.enabled ? 'Enabled' : 'Disabled'}
                />
              ) : null}
            </button>,
          ])
        ) : (
          <div className="empty">
            <h2>{isLoading ? 'Loading…' : 'No items'}</h2>
          </div>
        )}
      </div>
    </section>
  );
}
